import { createContext, useContext, useMemo, type CSSProperties, type ReactNode } from 'react'
import Box from '@mui/material/Box'
import { ThemeProvider, createTheme, useTheme } from '@mui/material/styles'

/**
 * Signal Provider sign-up accent — from brushfire-web-marketing
 * (`--color-accent` / CTA `#FF9933`, hover `#EF773D`, soft `#FFF4E0`).
 * Scoped to guest sign-up screens only; the rest of Provider stays blue.
 */
export const SP_SIGNUP_YELLOW = '#FF9933'
export const SP_SIGNUP_YELLOW_HOVER = '#EF773D'
export const SP_SIGNUP_YELLOW_SOFT = '#FFF4E0'
export const SP_SIGNUP_YELLOW_RING = 'rgba(255, 153, 51, 0.2)'
export const SP_SIGNUP_NAVY = '#122740'
/** Figma 140:6318 — label on orange primary CTAs (not white). */
export const SP_SIGNUP_ON_YELLOW = SP_SIGNUP_NAVY
/** Figma 132:1329 — survey-first OTP cells */
export const SURVEY_FIRST_OTP_CELL_BG = '#FEF8ED'
export const SURVEY_FIRST_OTP_CELL_WIDTH_PX = 65
export const SURVEY_FIRST_OTP_CELL_HEIGHT_PX = 64

const SignupShareThemeContext = createContext(false)

export function useSignupShareTheme(): boolean {
  return useContext(SignupShareThemeContext)
}

export function SignupShareThemeProvider({ children }: Readonly<{ children: ReactNode }>) {
  const outer = useTheme()
  const theme = useMemo(
    () =>
      createTheme(outer, {
        palette: {
          primary: {
            main: SP_SIGNUP_YELLOW,
            dark: SP_SIGNUP_YELLOW_HOVER,
            contrastText: SP_SIGNUP_ON_YELLOW,
          },
        },
      }),
    [outer],
  )

  return (
    <SignupShareThemeContext.Provider value={true}>
      <ThemeProvider theme={theme}>
        <Box
          data-testid="sp-signup-theme"
          style={
            {
              '--sp-survey-accent': SP_SIGNUP_YELLOW,
              '--sp-survey-on-accent': SP_SIGNUP_ON_YELLOW,
            } as CSSProperties
          }
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
          }}
        >
          {children}
        </Box>
      </ThemeProvider>
    </SignupShareThemeContext.Provider>
  )
}

export const signupSharePrimaryButtonSx = {
  '&:not(.Mui-disabled)': {
    bgcolor: SP_SIGNUP_YELLOW,
    color: SP_SIGNUP_ON_YELLOW,
    '&:hover': {
      bgcolor: SP_SIGNUP_YELLOW_HOVER,
      color: SP_SIGNUP_ON_YELLOW,
      opacity: 1,
    },
  },
} as const

/** Overrides survey-entry Start/Continue hardcoded blue when guest theme is on. */
export const signupShareSurveyStartButtonSx = {
  bgcolor: SP_SIGNUP_YELLOW,
  backgroundColor: SP_SIGNUP_YELLOW,
  color: SP_SIGNUP_ON_YELLOW,
  '&:hover': {
    bgcolor: SP_SIGNUP_YELLOW_HOVER,
    backgroundColor: SP_SIGNUP_YELLOW_HOVER,
    color: SP_SIGNUP_ON_YELLOW,
  },
  '&.Mui-disabled': {
    bgcolor: SP_SIGNUP_YELLOW,
    backgroundColor: SP_SIGNUP_YELLOW,
    color: SP_SIGNUP_ON_YELLOW,
    opacity: 0.45,
  },
} as const

export const signupShareOutlinedButtonSx = {
  border: `1px solid ${SP_SIGNUP_YELLOW}`,
  color: SP_SIGNUP_YELLOW,
  bgcolor: 'transparent',
  '&:hover': {
    bgcolor: SP_SIGNUP_YELLOW_SOFT,
    borderColor: SP_SIGNUP_YELLOW,
  },
} as const

export const signupShareContactInputSx = {
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: SP_SIGNUP_YELLOW,
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: SP_SIGNUP_YELLOW,
    borderWidth: '1px',
  },
  '&.Mui-focused': {
    boxShadow: `0 0 0 4px ${SP_SIGNUP_YELLOW_RING}`,
  },
} as const

export const signupShareLinkSx = {
  color: SP_SIGNUP_YELLOW,
  '& a': {
    color: SP_SIGNUP_YELLOW,
  },
} as const

/** Figma 136:4385 — “log in” is #F59E0B; rest stays slate. */
export const signupShareBackToLoginEmphasisSx = {
  color: '#F59E0B',
  fontWeight: 400,
} as const

export const signupShareOtpCellSx = {
  bgcolor: SURVEY_FIRST_OTP_CELL_BG,
  backgroundColor: SURVEY_FIRST_OTP_CELL_BG,
  maxWidth: SURVEY_FIRST_OTP_CELL_WIDTH_PX,
  height: { xs: 48, sm: SURVEY_FIRST_OTP_CELL_HEIGHT_PX },
  '&:focus-within': {
    borderColor: SP_SIGNUP_YELLOW,
  },
  '& input': {
    color: SP_SIGNUP_NAVY,
    fontWeight: 400,
  },
} as const

export const signupShareChannelSelectedSx = {
  border: `2px solid ${SP_SIGNUP_YELLOW}`,
  bgcolor: SP_SIGNUP_YELLOW_SOFT,
} as const

export const signupShareChannelLabelSelectedSx = {
  color: SP_SIGNUP_YELLOW,
} as const

export const signupShareChannelCheckmarkSx = {
  bgcolor: SP_SIGNUP_YELLOW,
  color: SP_SIGNUP_ON_YELLOW,
} as const
